import { create } from 'zustand'
import type { FilterGroup } from '@/tableControls'
import type {
  ConflictGroupSummary,
  ConflictStats,
  QboTransactionSearchCriteria,
  QboTransactionSearchRow,
  QboTransactionSearchStats,
} from '@/services/qboTransactionSearch'

export const QBO_TXN_SEARCH_CACHE_KEY = 'bookkeeper:qbo-txn-search-cache:v6'
export const QBO_TXN_SEARCH_CACHE_TTL_MS = 60 * 60 * 1000

export type QboTxnSearchTabId = 'basic' | 'conflicts'

export interface QboTxnSearchTabSlice {
  criteria: QboTransactionSearchCriteria
  /** Snapshot of criteria when the current result set was fetched from QBO. */
  searchedCriteria: QboTransactionSearchCriteria | null
  /** Client-side filters applied to loaded rows (starts blank after each search). */
  resultFilters: QboTransactionSearchCriteria
  resultFilterTree: FilterGroup | null
  resultTextSearch: string
  rows: QboTransactionSearchRow[]
  searchStats: QboTransactionSearchStats | null
  conflictGroups: ConflictGroupSummary[] | null
  conflictStats: ConflictStats | null
  showResults: boolean
}

interface CachedPayload {
  savedAt: number
  activeTab: QboTxnSearchTabId
  basic: QboTxnSearchTabSlice
  conflicts: QboTxnSearchTabSlice
}

interface QboTransactionSearchState {
  activeTab: QboTxnSearchTabId
  basic: QboTxnSearchTabSlice
  conflicts: QboTxnSearchTabSlice
  cachedAt: number | null

  setActiveTab: (tab: QboTxnSearchTabId) => void
  setCriteria: (
    criteria:
      | QboTransactionSearchCriteria
      | ((prev: QboTransactionSearchCriteria) => QboTransactionSearchCriteria),
    tab?: QboTxnSearchTabId
  ) => void
  setResultFilters: (
    filters:
      | QboTransactionSearchCriteria
      | ((prev: QboTransactionSearchCriteria) => QboTransactionSearchCriteria),
    tab?: QboTxnSearchTabId
  ) => void
  clearResultFilters: (tab?: QboTxnSearchTabId) => void
  setResultFilterTree: (tree: FilterGroup | null, tab?: QboTxnSearchTabId) => void
  setResultTextSearch: (text: string, tab?: QboTxnSearchTabId) => void
  setSearchResults: (
    rows: QboTransactionSearchRow[],
    criteria: QboTransactionSearchCriteria,
    searchStats?: QboTransactionSearchStats | null,
    conflictGroups?: ConflictGroupSummary[] | null,
    conflictStats?: ConflictStats | null
  ) => void
  updateRows: (rows: QboTransactionSearchRow[], tab?: QboTxnSearchTabId) => void
  reset: () => void
  isCacheValid: () => boolean
  expireIfStale: () => boolean
}

export const emptyBasicCriteria: QboTransactionSearchCriteria = {
  date_start: '',
  date_end: '',
  contact: [],
  description: '',
  doc_number: '',
  line_description: '',
  line_location: '',
  line_category: '',
  line_amount_op: 'eq',
  line_amount: '',
  memo: '',
  row_change_filter: 'all',
  category_conflicts_mode: false,
  conflict_similarity: 90,
}

/** Conflict-tab criteria: only dates + similarity are meaningful. */
export const emptyConflictCriteria: QboTransactionSearchCriteria = {
  date_start: '',
  date_end: '',
  category_conflicts_mode: true,
  conflict_similarity: 90,
}

function emptyBasicSlice(): QboTxnSearchTabSlice {
  return {
    criteria: { ...emptyBasicCriteria },
    searchedCriteria: null,
    resultFilters: { ...emptyBasicCriteria },
    resultFilterTree: null,
    resultTextSearch: '',
    rows: [],
    searchStats: null,
    conflictGroups: null,
    conflictStats: null,
    showResults: false,
  }
}

function emptyConflictSlice(): QboTxnSearchTabSlice {
  return {
    criteria: { ...emptyConflictCriteria },
    searchedCriteria: null,
    resultFilters: { ...emptyBasicCriteria },
    resultFilterTree: null,
    resultTextSearch: '',
    rows: [],
    searchStats: null,
    conflictGroups: null,
    conflictStats: null,
    showResults: false,
  }
}

function normalizeTabSlice(slice: QboTxnSearchTabSlice): QboTxnSearchTabSlice {
  return {
    ...slice,
    resultTextSearch: slice.resultTextSearch ?? '',
    resultFilterTree: slice.resultFilterTree ?? null,
  }
}

function isValidTabSlice(value: unknown): value is QboTxnSearchTabSlice {
  if (!value || typeof value !== 'object') return false
  const slice = value as Record<string, unknown>
  return (
    slice.criteria != null &&
    typeof slice.criteria === 'object' &&
    Array.isArray(slice.rows) &&
    typeof slice.showResults === 'boolean'
  )
}

function loadFromStorage(): CachedPayload | null {
  try {
    if (typeof localStorage === 'undefined') return null
    const raw = localStorage.getItem(QBO_TXN_SEARCH_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<CachedPayload>
    if (
      !parsed?.savedAt ||
      (parsed.activeTab !== 'basic' && parsed.activeTab !== 'conflicts') ||
      !isValidTabSlice(parsed.basic) ||
      !isValidTabSlice(parsed.conflicts)
    ) {
      localStorage.removeItem(QBO_TXN_SEARCH_CACHE_KEY)
      return null
    }
    if (Date.now() - parsed.savedAt >= QBO_TXN_SEARCH_CACHE_TTL_MS) {
      localStorage.removeItem(QBO_TXN_SEARCH_CACHE_KEY)
      return null
    }
    return {
      savedAt: parsed.savedAt,
      activeTab: parsed.activeTab,
      basic: normalizeTabSlice(parsed.basic),
      conflicts: normalizeTabSlice(parsed.conflicts),
    }
  } catch {
    try {
      localStorage.removeItem(QBO_TXN_SEARCH_CACHE_KEY)
    } catch {
      // Ignore storage errors (privacy mode, SSR, test env).
    }
    return null
  }
}

function saveToStorage(payload: CachedPayload): void {
  try {
    localStorage.setItem(QBO_TXN_SEARCH_CACHE_KEY, JSON.stringify(payload))
  } catch {
    // Ignore storage errors.
  }
}

const initialCache = loadFromStorage()

function persistState(state: {
  cachedAt: number | null
  activeTab: QboTxnSearchTabId
  basic: QboTxnSearchTabSlice
  conflicts: QboTxnSearchTabSlice
}): void {
  if (state.cachedAt == null) return
  saveToStorage({
    savedAt: state.cachedAt,
    activeTab: state.activeTab,
    basic: state.basic,
    conflicts: state.conflicts,
  })
}

function tabKey(tab: QboTxnSearchTabId): 'basic' | 'conflicts' {
  return tab
}

function tabForCriteria(criteria: QboTransactionSearchCriteria): QboTxnSearchTabId {
  return criteria.category_conflicts_mode ? 'conflicts' : 'basic'
}

export const useQboTransactionSearchStore = create<QboTransactionSearchState>((set, get) => ({
  activeTab: initialCache?.activeTab ?? 'basic',
  basic: initialCache?.basic ? normalizeTabSlice(initialCache.basic) : emptyBasicSlice(),
  conflicts: initialCache?.conflicts
    ? normalizeTabSlice(initialCache.conflicts)
    : emptyConflictSlice(),
  cachedAt: initialCache?.savedAt ?? null,

  setActiveTab: (tab) => {
    const state = get()
    const next = { ...state, activeTab: tab }
    persistState(next)
    set({ activeTab: tab })
  },

  setCriteria: (criteria, tab) =>
    set((state) => {
      const target = tab ?? state.activeTab
      const key = tabKey(target)
      const prevSlice = state[key]
      const nextCriteria =
        typeof criteria === 'function' ? criteria(prevSlice.criteria) : criteria
      // Keep mode flag consistent with the tab slice.
      const normalized: QboTransactionSearchCriteria =
        target === 'conflicts'
          ? { ...nextCriteria, category_conflicts_mode: true }
          : { ...nextCriteria, category_conflicts_mode: false }
      return {
        [key]: { ...prevSlice, criteria: normalized },
      }
    }),

  setResultFilters: (filters, tab) =>
    set((state) => {
      const target = tab ?? state.activeTab
      const key = tabKey(target)
      const prevSlice = state[key]
      const resultFilters =
        typeof filters === 'function' ? filters(prevSlice.resultFilters) : filters
      const nextSlice = { ...prevSlice, resultFilters }
      const next = { ...state, [key]: nextSlice }
      persistState(next)
      return { [key]: nextSlice }
    }),

  clearResultFilters: (tab) => {
    const state = get()
    const target = tab ?? state.activeTab
    const key = tabKey(target)
    const nextSlice = {
      ...state[key],
      resultFilters: { ...emptyBasicCriteria },
      resultFilterTree: null,
    }
    persistState({ ...state, [key]: nextSlice })
    set({ [key]: nextSlice })
  },

  setResultFilterTree: (tree, tab) =>
    set((state) => {
      const target = tab ?? state.activeTab
      const key = tabKey(target)
      const nextSlice = { ...state[key], resultFilterTree: tree }
      const next = { ...state, [key]: nextSlice }
      persistState(next)
      return { [key]: nextSlice }
    }),

  setResultTextSearch: (text, tab) =>
    set((state) => {
      const target = tab ?? state.activeTab
      const key = tabKey(target)
      const nextSlice = { ...state[key], resultTextSearch: text }
      const next = { ...state, [key]: nextSlice }
      persistState(next)
      return { [key]: nextSlice }
    }),

  setSearchResults: (rows, criteria, searchStats = null, conflictGroups = null, conflictStats = null) => {
    const state = get()
    const target = tabForCriteria(criteria)
    const key = tabKey(target)
    const savedAt = Date.now()
    const searchedCriteria = { ...criteria }
    const resultFilters = { ...emptyBasicCriteria }
    const nextSlice: QboTxnSearchTabSlice = {
      ...state[key],
      rows,
      criteria,
      searchedCriteria,
      resultFilters,
      resultFilterTree: null,
      resultTextSearch: '',
      searchStats,
      conflictGroups,
      conflictStats,
      showResults: true,
    }
    const next = {
      ...state,
      [key]: nextSlice,
      cachedAt: savedAt,
    }
    saveToStorage({
      savedAt,
      activeTab: state.activeTab,
      basic: next.basic,
      conflicts: next.conflicts,
    })
    set({
      [key]: nextSlice,
      cachedAt: savedAt,
    })
  },

  updateRows: (rows, tab) => {
    const state = get()
    const target = tab ?? state.activeTab
    const key = tabKey(target)
    const nextSlice = { ...state[key], rows }
    persistState({ ...state, [key]: nextSlice })
    set({ [key]: nextSlice })
  },

  reset: () => {
    try {
      localStorage.removeItem(QBO_TXN_SEARCH_CACHE_KEY)
    } catch {
      // Ignore storage errors.
    }
    set({
      activeTab: 'basic',
      basic: emptyBasicSlice(),
      conflicts: emptyConflictSlice(),
      cachedAt: null,
    })
  },

  isCacheValid: () => {
    const { cachedAt } = get()
    if (cachedAt == null) return false
    return Date.now() - cachedAt < QBO_TXN_SEARCH_CACHE_TTL_MS
  },

  expireIfStale: () => {
    if (get().isCacheValid()) return false
    get().reset()
    return true
  },
}))
